<template>
    <div class="health-sidebar">
        <h1 class="health-sidebar__title font-weight-bold">
            {{ toSentenceCase($t('planning.healthSidebar.title')) }}
        </h1>
        <div class="health-sidebar__title-row">
            <h4>{{ toSentenceCase($t('planning.healthSidebar.incrementalSales')) }}</h4>
            <h4>{{ toSentenceCase($t('planning.healthSidebar.incrementalMargin')) }}</h4>
        </div>
        <p class="health-sidebar__message">{{ $t('planning.healthSidebar.message') }}</p>
        <v-divider />

        <div class="summary-container">
            <div class="summary-container__promo-metric-item">
                <health-sidebar-kpi-item :kpi="yearToDateAggregatedMetrics.incrementalSales" />
            </div>
            <div class="summary-container__promo-metric-item">
                <health-sidebar-kpi-item :kpi="yearToDateAggregatedMetrics.incrementalMargin" />
            </div>
            <div
                class="summary-container__drop-down-item health-sidebar__expand-kpis promo-metric__drop-down"
            >
                <main-expand-button
                    :is-expanded="isExpanded"
                    background
                    expanded-icon="mdi-chevron-up"
                    collapsed-icon="mdi-chevron-down"
                    @expand="toggleKpis"
                />
            </div>
        </div>

        <div v-if="isExpanded" class="health-sidebar__details">
            <v-divider />
            <div class="health-sidebar__details-row">
                <h4 class="health-sidebar__details-column text-right">
                    {{ totalSales(yearToDateAggregatedMetrics.incrementalSales.delta) }}
                </h4>
                <h4 class="health-sidebar__details-column text-left">
                    {{ totalMargin(yearToDateAggregatedMetrics.incrementalMargin.delta) }}
                </h4>
                <div>
                    <v-icon size="15">mdi-delta</v-icon>
                </div>
            </div>
            <v-divider />
            <div class="health-sidebar__details-row">
                <div class="health-sidebar__details-column text-right">
                    {{ toSentenceCase($t('planning.healthSidebar.sales')) }}:
                </div>
                <div class="health-sidebar__details-column text-left">
                    {{ toSentenceCase($t('planning.healthSidebar.margin')) }}:
                </div>
            </div>
            <div class="health-sidebar__details-row">
                <h4 class="health-sidebar__details-column text-right">
                    {{ totalSales(yearToDateAggregatedMetrics.promoSales.value) }}
                </h4>
                <h4 class="health-sidebar__details-column text-left">
                    {{ totalMargin(yearToDateAggregatedMetrics.promoMargin.value) }}
                </h4>
            </div>
            <div class="health-sidebar__details-row">
                <h4 class="health-sidebar__details-column text-right">
                    {{
                        $n(
                            'numbers.default.percentageEvolution',
                            yearToDateAggregatedMetrics.promoSales.efficiency
                        )
                    }}
                    <v-icon size="12">
                        {{
                            getEfficiencyArrowClass(
                                yearToDateAggregatedMetrics.promoSales.efficiency
                            )
                        }}
                    </v-icon>
                </h4>
                <h4 class="health-sidebar__details-column text-left">
                    {{
                        $n(
                            'numbers.default.percentageEvolution',
                            yearToDateAggregatedMetrics.promoMargin.efficiency
                        )
                    }}
                    <v-icon size="12"
                        >{{
                            getEfficiencyArrowClass(
                                yearToDateAggregatedMetrics.promoMargin.efficiency
                            )
                        }}
                    </v-icon>
                </h4>
            </div>

            <v-divider />
            <div class="health-sidebar__details-row">
                <h4 class="health-sidebar__details-column text-right">
                    {{ totalSales(yearToDateAggregatedMetrics.promoSales.delta) }}
                </h4>
                <h4 class="health-sidebar__details-column text-left">
                    {{ totalMargin(yearToDateAggregatedMetrics.promoMargin.delta) }}
                </h4>
                <v-icon size="15">mdi-delta</v-icon>
            </div>
            <v-divider />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getArrowClass } from '../../utils/health-sidebar-utils';

export default {
    localizationKey: 'planning.plannerView',
    data() {
        return {
            isExpanded: false,
        };
    },
    computed: {
        ...mapGetters('promotions', ['getYearToDateAggregatedMetrics']),
        yearToDateAggregatedMetrics() {
            return this.getYearToDateAggregatedMetrics;
        },
    },
    methods: {
        toggleKpis() {
            this.isExpanded = !this.isExpanded;
            this.emitter.emit('summary-kpis-toggled');
        },
        getEfficiencyArrowClass(efficiency) {
            return getArrowClass({ kpi: efficiency });
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.health-sidebar {
    width: 100%;
    height: 100%;
    padding: 1rem;

    font-size: 1.2rem;
    background-color: $promo-white;

    &__expand-kpis {
        margin-top: 1rem;
        float: right;
    }

    &__title {
        font-size: 1.4rem;

        &-row {
            display: flex;
            margin: 0.5rem 0;

            h4 {
                width: 7rem;

                &:first-child {
                    margin-left: 0.5rem;
                }
            }
        }
    }

    &__message {
        margin-bottom: 0.5rem;
    }

    &__details {
        width: 17rem;
        padding-top: 1rem;
        text-align: center;

        &-row {
            display: flex;

            .mdi-delta {
                align-self: center;
            }
        }

        &-column {
            width: 7rem;
            padding: 0.2rem 1rem;

            .mdi-arrow-up {
                color: $rtls-postive-action-colour;
                -webkit-text-stroke: 0.1em $rtls-postive-action-colour;
            }

            .mdi-arrow-down {
                color: $rtls-negative-action-colour;
                -webkit-text-stroke: 0.1em $rtls-postive-action-colour;
            }
        }
    }
}

.summary-container {
    display: flex;

    &__promo-metric-item {
        width: 6.5rem;
        white-space: nowrap;
        margin-right: 1rem;
    }

    &__drop-down-item {
        flex: 1 0 0;
    }
}

.promo-metric {
    &__drop-down {
        display: flex;
        justify-content: flex-end;
        align-items: flex-end;
    }
}
</style>
